import React, { useState, useEffect } from 'react';
import { BookOpen, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GuidanceWidget = ({ assistantId }) => {
  const [guidance, setGuidance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState({
    days_1_7: false,
    days_8_14: false,
    days_15_30: false
  });

  useEffect(() => {
    fetchGuidance();
  }, [assistantId]);

  const fetchGuidance = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/guidance`, {
        headers: { 'x-assistant-session': assistantId }
      });
      const data = await res.json();
      
      if (data.ok && data.show_guidance) {
        setGuidance(data);
        // Auto-expand current phase
        setExpandedPhases(prev => ({
          ...prev,
          [data.current_phase]: true
        }));
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch guidance:', err);
      setLoading(false);
    }
  };

  const togglePhase = (phase) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phase]: !prev[phase]
    }));
  };

  const getPhaseStatus = (phase) => {
    if (!guidance) return null;
    
    const phaseOrder = ['days_1_7', 'days_8_14', 'days_15_30'];
    const currentIndex = phaseOrder.indexOf(guidance.current_phase);
    const targetIndex = phaseOrder.indexOf(phase);
    
    if (targetIndex < currentIndex) return 'completed';
    if (targetIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  if (loading) {
    return null;
  }

  if (!guidance || !guidance.show_guidance) {
    return null;
  }

  const phaseData = [
    { key: 'days_1_7', title: 'Days 1-7', subtitle: 'Foundation' },
    { key: 'days_8_14', title: 'Days 8-14', subtitle: 'Execution' },
    { key: 'days_15_30', title: 'Days 15-30', subtitle: 'Optimization' }
  ];

  return (
    <div style={{
      background: '#FFFFFF',
      borderRadius: '12px',
      border: '1px solid #E5E7EB',
      padding: '20px',
      marginBottom: '24px'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <BookOpen size={20} style={{ color: '#8B5CF6' }} />
          <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: 0 }}>
            First 30 Days Guide
          </h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
            Day {guidance.days_since_creation} of 30
          </p>
          <div style={{
            width: '100px',
            height: '6px',
            background: '#E5E7EB',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(guidance.days_since_creation / 30) * 100}%`,
              height: '100%',
              background: '#8B5CF6',
              transition: 'width 0.3s'
            }} />
          </div>
        </div>
      </div>

      {/* Phases */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {phaseData.map(phase => {
          const status = getPhaseStatus(phase.key);
          const isExpanded = expandedPhases[phase.key];
          const isCurrent = status === 'current';
          const isCompleted = status === 'completed';
          
          return (
            <div key={phase.key}>
              {/* Phase Header */}
              <div
                onClick={() => togglePhase(phase.key)}
                style={{
                  padding: '12px',
                  background: isCurrent ? '#F5F3FF' : isCompleted ? '#F0FDF4' : '#F9FAFB',
                  border: `1px solid ${isCurrent ? '#DDD6FE' : isCompleted ? '#BBF7D0' : '#E5E7EB'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: isCurrent ? '#8B5CF6' : isCompleted ? '#10B981' : '#E5E7EB',
                      color: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {isCompleted ? '✓' : phaseData.indexOf(phase) + 1}
                    </div>
                    <div>
                      <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', margin: 0 }}>
                        {phase.title}
                      </p>
                      <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
                        {phase.subtitle}
                      </p>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp size={20} style={{ color: '#6B7280' }} /> : <ChevronDown size={20} style={{ color: '#6B7280' }} />}
                </div>
              </div>

              {/* Phase Content */}
              {isExpanded && guidance.guidance[phase.key] && (
                <div style={{
                  marginTop: '8px',
                  marginLeft: '12px',
                  padding: '16px',
                  background: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ color: '#0A0A0A', fontSize: '15px', fontWeight: '600', margin: '0 0 12px 0' }}>
                    {guidance.guidance[phase.key].title}
                  </h4>
                  <p style={{ color: '#6B7280', fontSize: '13px', margin: '0 0 16px 0', lineHeight: '1.5' }}>
                    {guidance.guidance[phase.key].subtitle}
                  </p>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {guidance.guidance[phase.key].sections.map((section, idx) => (
                      <div key={idx}>
                        <h5 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: '0 0 6px 0' }}>
                          {section.topic}
                        </h5>
                        <p style={{ 
                          color: '#4B5563', 
                          fontSize: '13px', 
                          margin: 0, 
                          lineHeight: '1.6',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {section.content}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        background: '#F0F9FF',
        borderRadius: '8px',
        border: '1px solid #BFDBFE'
      }}>
        <p style={{ color: '#1E40AF', fontSize: '12px', margin: 0, lineHeight: '1.5' }}>
          💡 This guide helps you build momentum and confidence. It will automatically disappear after 30 days, but you can always ask your executive or admin for guidance.
        </p>
      </div>
    </div>
  );
};

export default GuidanceWidget;
